// External-database sync panel (Database Designer → "External Sync").
//
// Define mappings that keep a local table and a table in an external
// application database (a named project connection) updated on BOTH sides:
// push (twasta → external, via the outbox worker), pull (external → twasta,
// watermark polling) or bidirectional. Shows live status, failures and
// conflicts.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import {
  deleteSyncMapping,
  getSyncStatus,
  introspectRemote,
  listSyncConflicts,
  listSyncMappings,
  resolveSyncConflict,
  retrySyncOutbox,
  saveSyncMapping,
  syncNow,
  type SyncConflict,
  type SyncMapping,
  type SyncMappingStatus,
} from '@/api/databaseSync'
import { listProjectDbConnections, type ProjectDbConnection } from '@/api/projectDb'
import { getTables, getTableInfo } from '@/api/designers'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  margin: '10px 0 4px',
  color: 'var(--color-text-secondary)',
}

const chip: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  padding: '1px 8px',
  borderRadius: 999,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)',
}

function emptyMapping(): SyncMapping {
  return {
    mapping_name: '',
    connection_name: '',
    direction: 'bidirectional',
    local_table: '',
    remote_table: '',
    key_map: [{ local: '', remote: '' }],
    column_map: [{ local: '', remote: '' }],
    pull: { mode: 'watermark', watermark_column: '', interval_seconds: 60 },
    push: { interval_seconds: 10, max_attempts: 8 },
    conflict_policy: 'last_writer_wins',
    enabled: true,
  }
}

function fmtTime(t?: number | null): string {
  if (!t) return '—'
  return new Date(t * 1000).toLocaleTimeString()
}

export default function SyncPanel({
  project, open, onClose,
}: {
  project: string
  open: boolean
  onClose: () => void
}) {
  const [mappings, setMappings] = useState<SyncMapping[]>([])
  const [status, setStatus] = useState<SyncMappingStatus[]>([])
  const [conflicts, setConflicts] = useState<SyncConflict[]>([])
  const [connections, setConnections] = useState<ProjectDbConnection[]>([])
  const [localTables, setLocalTables] = useState<string[]>([])
  const [localCols, setLocalCols] = useState<string[]>([])
  const [remoteTables, setRemoteTables] = useState<string[]>([])
  const [remoteCols, setRemoteCols] = useState<string[]>([])
  const [draft, setDraft] = useState<SyncMapping | null>(null)
  const [saving, setSaving] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const [maps, st, conns, confl] = await Promise.all([
        listSyncMappings(project),
        getSyncStatus(project),
        listProjectDbConnections(project),
        listSyncConflicts(project),
      ])
      setMappings(maps)
      setStatus(st)
      setConnections(conns)
      setConflicts(confl)
    } catch (err) {
      toast.error(`Could not load sync config: ${err instanceof Error ? err.message : err}`)
    }
  }, [project])

  useEffect(() => {
    if (!open) return
    void refresh()
    const timer = window.setInterval(() => {
      void getSyncStatus(project).then(setStatus).catch(() => undefined)
    }, 10000)
    return () => window.clearInterval(timer)
  }, [open, project, refresh])

  // Local table list for the editor.
  useEffect(() => {
    if (!open) return
    getTables(project)
      .then((res: { tables?: Array<{ name: string }> }) =>
        setLocalTables((res.tables || []).map((t) => t.name)))
      .catch(() => setLocalTables([]))
  }, [open, project])

  // Local columns when the draft's table changes.
  useEffect(() => {
    const table = draft?.local_table
    if (!table) { setLocalCols([]); return }
    getTableInfo(project, table)
      .then((res: { table?: { columns?: Array<{ column_name?: string; name?: string }> } }) => {
        const cols = (res.table?.columns || [])
          .map((c) => (c.column_name || c.name || '').toLowerCase())
          .filter(Boolean)
        setLocalCols(cols)
      })
      .catch(() => setLocalCols([]))
  }, [draft?.local_table, project])

  // Remote tables when the draft's connection changes.
  useEffect(() => {
    const conn = draft?.connection_name
    if (!conn) { setRemoteTables([]); return }
    introspectRemote(project, conn, undefined, draft?.remote_schema)
      .then((res) => setRemoteTables(res.tables || []))
      .catch(() => setRemoteTables([]))
  }, [draft?.connection_name, draft?.remote_schema, project])

  // Remote columns when the draft's remote table changes.
  useEffect(() => {
    const conn = draft?.connection_name
    const table = draft?.remote_table
    if (!conn || !table) { setRemoteCols([]); return }
    introspectRemote(project, conn, table, draft?.remote_schema)
      .then((res) => setRemoteCols((res.columns || []).map((c) => c.name)))
      .catch(() => setRemoteCols([]))
  }, [draft?.connection_name, draft?.remote_table, draft?.remote_schema, project])

  const patch = (p: Partial<SyncMapping>) =>
    setDraft((d) => (d ? { ...d, ...p } : d))

  const handleSave = async () => {
    if (!draft) return
    setSaving(true)
    try {
      const clean: SyncMapping = {
        ...draft,
        key_map: draft.key_map.filter((k) => k.local && k.remote),
        column_map: draft.column_map.filter((c) => c.local && c.remote),
      }
      const res = await saveSyncMapping(project, clean)
      if (res.issues?.length) {
        toast.error(`Saved with issues: ${res.issues.join('; ')}`)
      } else {
        toast.success(`Mapping "${draft.mapping_name}" saved`)
      }
      setDraft(null)
      await refresh()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail
      toast.error(detail || `Save failed: ${err instanceof Error ? err.message : err}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Delete sync mapping "${name}"?`)) return
    try {
      await deleteSyncMapping(project, name)
      toast.success(`Deleted "${name}"`)
      await refresh()
    } catch (err) {
      toast.error(`Delete failed: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleSyncNow = async () => {
    try {
      await syncNow(project)
      toast.success('Sync triggered')
      window.setTimeout(() => { void refresh() }, 1500)
    } catch (err) {
      toast.error(`Sync failed: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleRetry = async () => {
    try {
      const res = await retrySyncOutbox(project)
      toast.success(`Retrying ${res.retried} failed row(s)`)
      await refresh()
    } catch (err) {
      toast.error(`Retry failed: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleResolve = async (c: SyncConflict, keep: 'local' | 'remote') => {
    try {
      await resolveSyncConflict(project, c.id, keep)
      toast.success(`Conflict resolved (kept ${keep})`)
      await refresh()
    } catch (err) {
      toast.error(`Resolve failed: ${err instanceof Error ? err.message : err}`)
    }
  }

  const statusFor = (name: string) =>
    status.find((s) => s.mapping_name === name)

  const pairEditor = (
    pairs: Array<{ local: string; remote: string }>,
    onChange: (pairs: Array<{ local: string; remote: string }>) => void,
  ) => (
    <div style={{ display: 'grid', gap: 6 }}>
      {pairs.map((p, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
          <input
            style={inputStyle}
            list="sync-local-cols"
            placeholder="local column"
            value={p.local}
            onChange={(e) => {
              const next = [...pairs]
              next[i] = { ...next[i], local: e.target.value }
              onChange(next)
            }}
          />
          <input
            style={inputStyle}
            list="sync-remote-cols"
            placeholder="remote column"
            value={p.remote}
            onChange={(e) => {
              const next = [...pairs]
              next[i] = { ...next[i], remote: e.target.value }
              onChange(next)
            }}
          />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onChange(pairs.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      ))}
      <div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => onChange([...pairs, { local: '', remote: '' }])}
        >
          + Add column
        </button>
      </div>
    </div>
  )

  return (
    <Modal open={open} onClose={onClose} title="External database sync" size="wide">
      <datalist id="sync-local-cols">
        {localCols.map((c) => <option key={c} value={c} />)}
      </datalist>
      <datalist id="sync-remote-cols">
        {remoteCols.map((c) => <option key={c} value={c} />)}
      </datalist>

      <div style={{ display: 'grid', gap: 12 }}>
        {!draft && (
          <>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setDraft(emptyMapping())}
                disabled={connections.length === 0}
              >
                + Add mapping
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => void handleSyncNow()}>
                Sync now
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => void handleRetry()}>
                Retry failed
              </button>
            </div>
            {connections.length === 0 && (
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                Define a database connection first (Database connections panel) —
                sync mappings push/pull against a named connection.
              </p>
            )}

            {mappings.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                No sync mappings. Add one to keep a table synchronized with an
                external application database (e.g. a twasta app on Postgres
                extending an existing Oracle system).
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {mappings.map((m) => {
                  const st = statusFor(m.mapping_name)
                  const pending = st?.outbox?.pending || 0
                  const failed = st?.outbox?.failed || 0
                  return (
                    <div
                      key={m.mapping_name}
                      style={{
                        padding: '8px 12px', borderRadius: 8,
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ fontSize: '0.9125rem' }}>{m.mapping_name}</strong>
                        <span style={chip}>{m.direction}</span>
                        <span style={chip}>
                          {m.local_table} ↔ {m.remote_schema ? `${m.remote_schema}.` : ''}{m.remote_table}
                        </span>
                        <span style={chip}>{m.connection_name}</span>
                        {!m.enabled && <span style={{ ...chip, opacity: 0.7 }}>disabled</span>}
                        <span style={{ flex: 1 }} />
                        <button className="btn btn-ghost btn-sm" onClick={() => setDraft({ ...m })}>
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => void handleDelete(m.mapping_name)}
                        >
                          Delete
                        </button>
                      </div>
                      {(m._issues?.length || 0) > 0 && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #d33)', marginTop: 4 }}>
                          ⚠ {m._issues!.join('; ')}
                        </div>
                      )}
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                        push {fmtTime(st?.last_push_at)} · pull {fmtTime(st?.last_pull_at)}
                        {pending > 0 && <> · <strong>{pending} pending</strong></>}
                        {failed > 0 && (
                          <> · <strong style={{ color: 'var(--color-error, #d33)' }}>{failed} failed</strong></>
                        )}
                        {st?.last_error && (
                          <> · <span style={{ color: 'var(--color-error, #d33)' }}>{st.last_error}</span></>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {conflicts.length > 0 && (
              <div>
                <h4 style={{ fontSize: '0.875rem', margin: '8px 0 6px' }}>
                  Conflicts ({conflicts.length})
                </h4>
                <div style={{ display: 'grid', gap: 6 }}>
                  {conflicts.map((c) => (
                    <div
                      key={String(c.id)}
                      style={{
                        padding: '8px 12px', borderRadius: 8,
                        border: '1px solid var(--color-error, #d33)',
                        fontSize: '0.8125rem',
                      }}
                    >
                      <div style={{ marginBottom: 4 }}>
                        <strong>{c.mapping_name}</strong> · record {c.local_pk} changed on BOTH sides
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <pre style={{ margin: 0, overflow: 'auto', maxHeight: 90 }}>
                          local: {JSON.stringify(c.local_values, null, 1)}
                        </pre>
                        <pre style={{ margin: 0, overflow: 'auto', maxHeight: 90 }}>
                          remote: {JSON.stringify(c.remote_values, null, 1)}
                        </pre>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => void handleResolve(c, 'local')}>
                          Keep local
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => void handleResolve(c, 'remote')}>
                          Keep remote
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {draft && (
          <div>
            <label style={labelStyle}>Mapping name</label>
            <input
              style={inputStyle}
              value={draft.mapping_name}
              placeholder="e.g. orders_to_erp"
              onChange={(e) => patch({ mapping_name: e.target.value })}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelStyle}>Connection</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={draft.connection_name}
                  onChange={(e) => patch({ connection_name: e.target.value })}
                >
                  <option value="">— pick a connection —</option>
                  {connections.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name} ({c.db_type})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Direction</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={draft.direction}
                  onChange={(e) =>
                    patch({ direction: e.target.value as SyncMapping['direction'] })}
                >
                  <option value="push">Push (twasta → external)</option>
                  <option value="pull">Pull (external → twasta)</option>
                  <option value="bidirectional">Bidirectional</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelStyle}>Local table</label>
                <input
                  style={inputStyle}
                  list="sync-local-tables"
                  value={draft.local_table}
                  onChange={(e) => patch({ local_table: e.target.value })}
                />
                <datalist id="sync-local-tables">
                  {localTables.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <label style={labelStyle}>Remote schema (optional)</label>
                <input
                  style={inputStyle}
                  value={draft.remote_schema || ''}
                  onChange={(e) => patch({ remote_schema: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle}>Remote table</label>
                <input
                  style={inputStyle}
                  list="sync-remote-tables"
                  value={draft.remote_table}
                  onChange={(e) => patch({ remote_table: e.target.value })}
                />
                <datalist id="sync-remote-tables">
                  {remoteTables.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
            </div>

            <label style={labelStyle}>Key columns (matching rows on both sides)</label>
            {pairEditor(draft.key_map, (key_map) => patch({ key_map }))}

            <label style={labelStyle}>Synced columns</label>
            {pairEditor(
              draft.column_map as Array<{ local: string; remote: string }>,
              (column_map) => patch({ column_map }),
            )}

            {draft.direction !== 'push' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={labelStyle}>Pull mode</label>
                    <select
                      style={{ ...inputStyle, cursor: 'pointer' }}
                      value={draft.pull?.mode || 'watermark'}
                      onChange={(e) =>
                        patch({ pull: { ...draft.pull, mode: e.target.value as 'watermark' | 'diff' } })}
                    >
                      <option value="watermark">Watermark column</option>
                      <option value="diff">Full diff (small tables)</option>
                    </select>
                  </div>
                  {(draft.pull?.mode || 'watermark') === 'watermark' && (
                    <div>
                      <label style={labelStyle}>Watermark column (remote)</label>
                      <input
                        style={inputStyle}
                        list="sync-remote-cols"
                        placeholder="e.g. LAST_UPDATED"
                        value={draft.pull?.watermark_column || ''}
                        onChange={(e) =>
                          patch({ pull: { ...draft.pull, watermark_column: e.target.value } })}
                      />
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>Pull every (seconds)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={draft.pull?.interval_seconds ?? 60}
                      onChange={(e) =>
                        patch({ pull: { ...draft.pull, interval_seconds: Number(e.target.value) || 60 } })}
                    />
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelStyle}>Conflict policy</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={draft.conflict_policy || 'last_writer_wins'}
                  onChange={(e) =>
                    patch({ conflict_policy: e.target.value as SyncMapping['conflict_policy'] })}
                >
                  <option value="last_writer_wins">Last writer wins</option>
                  <option value="twasta_wins">Twasta wins</option>
                  <option value="external_wins">External wins</option>
                  <option value="review">Park for review</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Enabled</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={draft.enabled === false ? 'no' : 'yes'}
                  onChange={(e) => patch({ enabled: e.target.value === 'yes' })}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 8 }}>
              Pulled rows are applied directly (transaction validations are not
              run) and recorded in the audit trail as sync changes. Pushes are
              queued atomically with each save and retried automatically when
              the external database is unreachable.
            </p>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void handleSave()}
                disabled={
                  saving
                  || !draft.mapping_name.trim()
                  || !draft.connection_name
                  || !draft.local_table
                  || !draft.remote_table
                }
              >
                {saving ? 'Saving…' : 'Save mapping'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setDraft(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
